import React from 'react';
import {
  Input,
  InputField,
  Select,
  SelectTrigger,
  SelectInput,
  SelectIcon,
  SelectPortal,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicatorWrapper,
  SelectDragIndicator,
  SelectItem,
  Text,
  VStack,
  ChevronDownIcon,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { MODULE_GRADE_OPTIONS } from '@/common/grading/gradeOptions';

export type GradeOverrideMode = 'set' | 'overwrite';

export interface GradeOverrideDialogProps {
  mode: GradeOverrideMode;
  attemptNumber?: number | null;
  initialGrade?: string;
  initialComment?: string;
  inlineError?: string | null;
  onGradeChange: (grade: string) => void;
  onCommentChange: (comment: string) => void;
}

const MAX_COMMENT_LENGTH = 160;

/**
 * GradeOverrideDialogBody
 * ----
 * Context-free dialog body for admin set/overwrite grade actions.
 */
export const GradeOverrideDialogBody = ({
  mode,
  attemptNumber,
  initialGrade = '',
  initialComment = '',
  inlineError,
  onGradeChange,
  onCommentChange,
}: GradeOverrideDialogProps) => {
  const { t } = useTranslation();
  const [grade, setGrade] = React.useState(initialGrade);
  const [comment, setComment] = React.useState(initialComment);

  React.useEffect(() => {
    setGrade(initialGrade);
  }, [initialGrade]);

  React.useEffect(() => {
    setComment(initialComment);
  }, [initialComment]);

  const handleGrade = (value: string) => {
    setGrade(value);
    onGradeChange(value);
  };

  const handleComment = (value: string) => {
    const singleLine = value.replace(/[\r\n]+/g, ' ');
    setComment(singleLine);
    onCommentChange(singleLine);
  };

  return (
    <VStack space="md" mt="$2">
      <Select selectedValue={grade} onValueChange={handleGrade} testID="admin-grade-override-grade-select">
        <SelectTrigger variant="outline" size="md">
          <SelectInput placeholder={t('admin.gradeOverride.gradePlaceholder')} />
          <SelectIcon as={ChevronDownIcon} mr="$3" />
        </SelectTrigger>
        <SelectPortal>
          <SelectBackdrop />
          <SelectContent>
            <SelectDragIndicatorWrapper>
              <SelectDragIndicator />
            </SelectDragIndicatorWrapper>
            {MODULE_GRADE_OPTIONS.map((option) => (
              <SelectItem key={option.value} label={option.label} value={option.value} />
            ))}
          </SelectContent>
        </SelectPortal>
      </Select>

      <Input>
        <InputField
          placeholder={t('admin.gradeOverride.commentPlaceholder')}
          value={comment}
          maxLength={MAX_COMMENT_LENGTH}
          onChangeText={handleComment}
        />
      </Input>

      <Text size="sm" color="$textSubtle">
        {t('admin.gradeOverride.notifyLine')}
      </Text>

      {mode === 'overwrite' && attemptNumber != null && (
        <Text size="sm" color="$textSubtle">
          {t('admin.gradeOverride.historyLine', { attempt: attemptNumber })}
        </Text>
      )}

      {!!inlineError && (
        <Text size="sm" color="$statusError">
          {inlineError}
        </Text>
      )}
    </VStack>
  );
};

export const isGradeOverrideFormValid = (grade: string, comment: string): boolean => {
  const trimmed = comment.trim();
  if (!grade || !trimmed) return false;
  if (trimmed.includes('\n') || trimmed.includes('\r')) return false;
  if (trimmed.length > MAX_COMMENT_LENGTH) return false;
  return MODULE_GRADE_OPTIONS.some((opt) => opt.value === grade);
};
